import { type AppProps } from "next/app"
import { useRouter } from "next/router"
import { type ReactElement, type ReactNode, useState } from "react"
import { ToastContainer } from "@octopus-energy/coral/atoms"
import { env } from "@oefr/env/client"
import { assert } from "@oefr/utils/assert"
import {
    type DehydratedState,
    HydrationBoundary,
    QueryClient,
    QueryClientProvider,
} from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { Analytics } from "@vercel/analytics/react"
import { SpeedInsights } from "@vercel/speed-insights/next"
import { VercelToolbar } from "@vercel/toolbar/next"
import { setDefaultOptions } from "date-fns"
import { fr } from "date-fns/locale"
import { type NextPage } from "next"
import { FeatureFlagsProvider } from "@/contexts/FeatureFlagsProvider"
import { Providers } from "@/contexts/Providers"
import { getDefaultHeadlessAppLayout } from "@/features/navigation"
import { type FeatureFlagValues } from "@/lib/flags/feature-flags.shared"
import { onboardingRoutesPaths } from "@/routes/onboarding/onboarding-routes"
import "../styles/panda.css"

setDefaultOptions({
    locale: fr,
})

/** Routes where the React Query devtools panel should not be rendered. */
const REACT_QUERY_DEVTOOLS_EXCLUDED_ROUTES: string[] = [onboardingRoutesPaths.quote.route]

type NextPageWithLayout<
    TProps = NonNullable<
        unknown & {
            headless?: boolean
        }
    >,
    TInitialProps = TProps,
> = NextPage<TProps, TInitialProps> & {
    getHeadlessLayout?: (page: ReactElement, headless?: boolean) => ReactNode
    getLayout: (page: ReactElement, headless?: boolean) => ReactNode
}

export type AppPropsWithLayout = AppProps<{
    messages?: IntlMessages
    headless?: boolean
    dehydratedState?: DehydratedState
    flags?: Partial<FeatureFlagValues>
}> & {
    Component: NextPageWithLayout
}

function usePageWithLayout({
    Component,
    pageProps,
}: Pick<AppPropsWithLayout, "Component" | "pageProps">) {
    const router = useRouter()

    let getLayout = Component.getLayout

    if (!!pageProps.headless) {
        getLayout = Component.getHeadlessLayout || getDefaultHeadlessAppLayout
    }

    assert(getLayout, `getLayout not set for page "${Component.displayName ?? router.pathname}".`)

    return getLayout(<Component {...pageProps} />)
}

export default function App({ Component, pageProps }: AppPropsWithLayout) {
    const { messages, dehydratedState } = pageProps
    const router = useRouter()

    const [queryClient] = useState(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        refetchOnMount: false,
                        refetchOnWindowFocus: false,
                        refetchOnReconnect: false,
                    },
                },
            })
    )

    const Page = usePageWithLayout({ Component, pageProps })

    const isDevtoolsExcludedRoute = REACT_QUERY_DEVTOOLS_EXCLUDED_ROUTES.includes(router.pathname)

    return (
        <>
            <QueryClientProvider client={queryClient}>
                <HydrationBoundary state={dehydratedState}>
                    <Providers messages={messages}>
                        <FeatureFlagsProvider flags={pageProps.flags}>{Page}</FeatureFlagsProvider>
                        {!isDevtoolsExcludedRoute && (
                            <ReactQueryDevtools
                                client={queryClient}
                                initialIsOpen={false}
                            />
                        )}

                        <ToastContainer duration={4000} />
                    </Providers>
                </HydrationBoundary>
            </QueryClientProvider>

            {env.NEXT_PUBLIC_VERCEL_ENV === "development" && <VercelToolbar />}
            {env.NEXT_PUBLIC_VERCEL_ENV === "production" && (
                <>
                    <Analytics />
                    <SpeedInsights />
                </>
            )}
        </>
    )
}
